import { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Search, Pencil, AlertTriangle, ShieldCheck, Clock, Download, FileWarning,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = {
  employee_name: "",
  nationality: "",
  designation: "",
  iqama_number: "",
  iqama_issue_date: "",
  iqama_expiry_date: "",
  passport_number: "",
  passport_issue_date: "",
  passport_expiry_date: "",
  notes: "",
};

const STATUS_OPTIONS = [
  { v: "all", label: "All records" },
  { v: "expired", label: "Expired" },
  { v: "expiring", label: "Expiring soon" },
  { v: "valid", label: "Valid" },
  { v: "missing", label: "Missing data" },
];

const THRESHOLDS = [
  { v: "30", label: "Alert: 30 days" },
  { v: "60", label: "Alert: 60 days" },
  { v: "90", label: "Alert: 90 days" },
];

export default function DocumentsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, expired: 0, expiring: 0, valid: 0, missing: 0 });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [threshold, setThreshold] = useState("30");

  const load = async () => {
    try {
      const [docsRes, sumRes] = await Promise.all([
        api.get(`/admin/documents?threshold_days=${threshold}`),
        api.get(`/admin/documents/summary?threshold_days=${threshold}`),
      ]);
      setItems(docsRes.data?.items || []);
      setCounts(sumRes.data?.counts || {});
    } catch {
      toast.error("Failed to load documents");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.employee_name.trim()) {
      toast.error("Employee name is required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/documents/${editing}`, form);
        toast.success("Document updated");
      } else {
        await api.post("/admin/documents", form);
        toast.success("Document added");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((d) => {
      const matchSearch = !q
        || d.employee_name.toLowerCase().includes(q)
        || (d.iqama_number || "").toLowerCase().includes(q)
        || (d.passport_number || "").toLowerCase().includes(q)
        || (d.designation || "").toLowerCase().includes(q);
      const matchFilter = filter === "all" || d.iqama_status === filter || d.passport_status === filter;
      return matchSearch && matchFilter;
    });
  }, [items, search, filter]);

  const exportCsv = () => {
    const headers = [
      "Employee Name", "Nationality", "Designation",
      "Iqama Number", "Iqama Issue", "Iqama Expiry", "Iqama Status", "Iqama Days Left",
      "Passport Number", "Passport Issue", "Passport Expiry", "Passport Status", "Passport Days Left",
      "Notes",
    ];
    const rows = filtered.map((d) => [
      d.employee_name, d.nationality || "", d.designation || "",
      d.iqama_number || "", d.iqama_issue_date || "", d.iqama_expiry_date || "", d.iqama_status, d.iqama_days_left ?? "",
      d.passport_number || "", d.passport_issue_date || "", d.passport_expiry_date || "", d.passport_status, d.passport_days_left ?? "",
      (d.notes || "").replace(/[\r\n,]+/g, " "),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mnh-documents-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="documents-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="overline">/ Compliance</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">
            Iqama & Passport <span className="text-primary">Tracker</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Track every employee's official documents and get alerted before expiry.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportCsv}
            className="rounded-sm h-11"
            data-testid="docs-export-csv"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={openCreate}
            className="rounded-sm h-11 bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground"
            data-testid="docs-add-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Document
          </Button>
        </div>
      </header>

      {/* Alert summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={AlertTriangle}
          label="Expired"
          value={counts.expired || 0}
          tone="danger"
          testid="docs-stat-expired"
        />
        <SummaryCard
          icon={Clock}
          label={`Expiring ≤ ${threshold} days`}
          value={counts.expiring || 0}
          tone="warning"
          testid="docs-stat-expiring"
        />
        <SummaryCard
          icon={ShieldCheck}
          label="Valid"
          value={counts.valid || 0}
          tone="success"
          testid="docs-stat-valid"
        />
        <SummaryCard
          icon={FileWarning}
          label="Missing data"
          value={counts.missing || 0}
          tone="muted"
          testid="docs-stat-missing"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name, iqama, passport, designation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm h-11 w-80 pl-9"
            data-testid="docs-search"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="rounded-sm h-11 w-56" data-testid="docs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (<SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={threshold} onValueChange={setThreshold}>
          <SelectTrigger className="rounded-sm h-11 w-44" data-testid="docs-threshold-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {THRESHOLDS.map((o) => (<SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="rounded-sm overflow-hidden">
        <Table data-testid="docs-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Employee</TableHead>
              <TableHead>Iqama Number</TableHead>
              <TableHead>Iqama Expiry</TableHead>
              <TableHead>Iqama Status</TableHead>
              <TableHead>Passport Number</TableHead>
              <TableHead>Passport Expiry</TableHead>
              <TableHead>Passport Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                No documents yet. Click "Add Document" to start tracking.
              </TableCell></TableRow>
            )}
            {filtered.map((d) => (
              <TableRow key={d.id} data-testid={`docs-row-${d.id}`}>
                <TableCell>
                  <div className="font-semibold">{d.employee_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.designation || "—"} · {d.nationality || "—"}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{d.iqama_number || "—"}</TableCell>
                <TableCell className="text-sm">{d.iqama_expiry_date || "—"}</TableCell>
                <TableCell><StatusBadge status={d.iqama_status} days={d.iqama_days_left} /></TableCell>
                <TableCell className="font-mono text-xs">{d.passport_number || "—"}</TableCell>
                <TableCell className="text-sm">{d.passport_expiry_date || "—"}</TableCell>
                <TableCell><StatusBadge status={d.passport_status} days={d.passport_days_left} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`docs-edit-${d.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid={`docs-delete-${d.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <p className="text-xs text-muted-foreground">
        Showing {filtered.length} of {items.length} records · alerts trigger {threshold} days before expiry.
      </p>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="docs-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit document record" : "Add document record"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Employee name *" span={2}>
              <Input value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} className="rounded-sm" data-testid="docs-input-employee_name" />
            </Field>
            <Field label="Designation">
              <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="rounded-sm" data-testid="docs-input-designation" />
            </Field>
            <Field label="Nationality">
              <Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className="rounded-sm" data-testid="docs-input-nationality" />
            </Field>

            <div className="col-span-2 mt-2 mb-1 text-xs uppercase tracking-[0.18em] text-primary font-bold">Iqama</div>
            <Field label="Iqama number">
              <Input value={form.iqama_number} onChange={(e) => setForm({ ...form, iqama_number: e.target.value })} className="rounded-sm font-mono" data-testid="docs-input-iqama_number" />
            </Field>
            <Field label="Iqama issue date">
              <Input type="date" value={form.iqama_issue_date} onChange={(e) => setForm({ ...form, iqama_issue_date: e.target.value })} className="rounded-sm" data-testid="docs-input-iqama_issue_date" />
            </Field>
            <Field label="Iqama expiry date" span={2}>
              <Input type="date" value={form.iqama_expiry_date} onChange={(e) => setForm({ ...form, iqama_expiry_date: e.target.value })} className="rounded-sm" data-testid="docs-input-iqama_expiry_date" />
            </Field>

            <div className="col-span-2 mt-2 mb-1 text-xs uppercase tracking-[0.18em] text-primary font-bold">Passport</div>
            <Field label="Passport number">
              <Input value={form.passport_number} onChange={(e) => setForm({ ...form, passport_number: e.target.value })} className="rounded-sm font-mono" data-testid="docs-input-passport_number" />
            </Field>
            <Field label="Passport issue date">
              <Input type="date" value={form.passport_issue_date} onChange={(e) => setForm({ ...form, passport_issue_date: e.target.value })} className="rounded-sm" data-testid="docs-input-passport_issue_date" />
            </Field>
            <Field label="Passport expiry date" span={2}>
              <Input type="date" value={form.passport_expiry_date} onChange={(e) => setForm({ ...form, passport_expiry_date: e.target.value })} className="rounded-sm" data-testid="docs-input-passport_expiry_date" />
            </Field>

            <Field label="Notes" span={2}>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" data-testid="docs-input-notes" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="docs-save-btn">
              {editing ? "Save changes" : "Add record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    danger: "bg-red-50 text-red-700 border-red-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    muted: "bg-muted text-foreground border-border",
  };
  return (
    <div className={`p-5 rounded-sm border ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <Icon className="h-5 w-5" />
        <div className="text-3xl font-black tabular-nums">{value}</div>
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.18em] font-semibold">{label}</div>
    </div>
  );
}

function StatusBadge({ status, days }) {
  if (status === "missing") {
    return <Badge variant="outline" className="rounded-sm">No data</Badge>;
  }
  if (status === "expired") {
    return (
      <Badge className="rounded-sm bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
        Expired {days != null ? `${Math.abs(days)}d ago` : ""}
      </Badge>
    );
  }
  if (status === "expiring") {
    return (
      <Badge className="rounded-sm bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
        Expires in {days}d
      </Badge>
    );
  }
  return (
    <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">
      Valid · {days}d left
    </Badge>
  );
}
